import { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

const PWAInstallPrompt = ({ appName, manifestUrl, themeColor = '#C4785A' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Update manifest link for this specific app
    const existingManifest = document.querySelector('link[rel="manifest"]');
    if (existingManifest && manifestUrl) {
      existingManifest.href = manifestUrl;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowPrompt(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [manifestUrl]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  // Don't show if already installed or no prompt available (and not iOS)
  if (!showPrompt && !isIOS) return null;

  return (
    <>
      {/* Install Button */}
      <button
        onClick={() => isIOS ? setShowIOSInstructions(true) : handleInstall()}
        className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium transition-all hover:opacity-90"
        style={{ backgroundColor: themeColor }}
      >
        <Download size={18} />
        Installer l'app
      </button>

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg text-gray-800">Installer {appName}</h3>
              <button 
                onClick={() => setShowIOSInstructions(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold">1</span>
                </div>
                <p className="text-gray-600 text-sm">
                  Appuyez sur le bouton <strong>Partager</strong> (icône carré avec flèche vers le haut) en bas de Safari
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold">2</span>
                </div>
                <p className="text-gray-600 text-sm">
                  Faites défiler et appuyez sur <strong>"Sur l'écran d'accueil"</strong>
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold">3</span>
                </div>
                <p className="text-gray-600 text-sm">
                  Appuyez sur <strong>"Ajouter"</strong> en haut à droite
                </p>
              </div>
            </div>
            
            <Button
              onClick={() => setShowIOSInstructions(false)}
              className="w-full mt-6"
              style={{ backgroundColor: themeColor }}
            >
              Compris !
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
